import React, { useState, useMemo, useRef } from 'react'
import { Row, Col } from 'react-flexbox-grid'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import ContentBlock, { ContentBlockLoading } from 'components/ContentBlock'
import { Form, Select, DateTimePicker, Number as NumberInput } from 'components/forms'
import DataTable from 'components/DataTableTest'
import Button from 'components/Button'
import Divider from 'components/Divider'
import { Modal, AlertModal } from 'components/Modal'
import ViewerPDF from 'components/ViewerPDF'
import { useLazyQuery, useMutation } from 'components/graphql'
import listCashBooks from './gql/listCashBooks'
import updateCashBook from './gql/updateCashBook'
import getCashBookReport from './gql/getCashBookReport'
import { listCashRegisters } from 'gql/query'
import getColumns from './tableColumns'
import { notEmpty } from 'helpers/validators'

const CashBookHandler = ({ appData }) => {
	const [params, setParams] = useState()
	const [isFetching, setIsFetching] = useState()
	const [selectedRecord, setSelectedRecord] = useState()
	const [closeReport, setCloseReport] = useState()
	const [reportError, setReportError] = useState()
	const selectedCR = useRef({})
	const [t] = useTranslation()

	const [updateRecord] = useMutation(updateCashBook)
	const [queryPdfReport, { data: reportData, loading: reportLoading }] = useLazyQuery(getCashBookReport, {
		onError: err => {
			let errMsg = t('common.strings.site_error')
			if(Array.isArray(err?.graphQLErrors)) {
				const isNoDataErr = err.graphQLErrors.some(inst => inst?.extensions?.code === 'BAD_USER_INPUT')
				if(isNoDataErr) {
					errMsg = t('common.strings.table_no_data')
				}
			}
			setReportError(errMsg)
		}
	})
	const tableColumns = useMemo(() => getColumns(t), [t])
	const initPeriod = {
		from: moment(new Date()).utc().startOf('day').toISOString(),
		to: moment(new Date()).utc().endOf('day').toISOString()
	}
	const queryList = vals => {
		setParams(vals)
		setIsFetching(true)
		setSelectedRecord()
	}
	const getPdf = formApi => e => {
		formApi.validate()
		const { invalid, values } = formApi.getState()
		if(!invalid) {
			const queryVars = {
				filter: values,
				params: {
					company: appData?.name,
					cashRegister: selectedCR?.current?.name
				}
			}
			setCloseReport(false)
			queryPdfReport({
				variables: queryVars
			})
		}
	}
	const handleUpdate = vals => {
		updateRecord({
			variables: {
				id: selectedRecord.id,
				fields: vals
			}
		})
	}

	if(reportData?.getCashBookReport && !closeReport) {
		return(
			<Modal unmount={() => setCloseReport(true)} fullScreen={true}>
				<ViewerPDF src={reportData.getCashBookReport} style={{ height: '100%' }} />
			</Modal>
		)
	}

	return(
		<>
			<ContentBlock>
				<Form initialValues={{ period: initPeriod }} onSubmit={queryList} smartSubmit={false}>
					{({ formApi }) => (
						<>
							<Row>
								<Col xs={3}>
									<Select
										field='linkCashRegister'
										label={t('common.fields.cash_register')}
										remoteOptions={{
											query: listCashRegisters
										}}
										valueField='id'
										labelField='name'
										onChange={val => {
											selectedCR.current = val
										}}
										isSearchable
										{...notEmpty()}
										validateOnChange
									/>
								</Col>
								<Col xs={3}>
									<DateTimePicker
										field='period'
										label={t('common.fields.period')}
										interval
									/>
								</Col>
							</Row>
							<Divider />
							<Button text={t('common.buttons.apply')} type='submit' />
							<Button text={t('cash_book_section.strings.print_as_pdf')} onClick={getPdf(formApi)} loading={reportLoading} />
						</>
					)}
				</Form>
			</ContentBlock>
			{params &&
				<ContentBlockLoading isLoading={isFetching}>
					<Row>
						<Col xs={10}>
							<DataTable
								label={t('cash_book_section.caption')}
								remoteOptions={{
									query: listCashBooks,
									variables: {
										filter: params
									},
									onCompleted: () => {
										if(isFetching) {
											setIsFetching(false)
										}
									},
									onError: () => setIsFetching(false)
								}}
								columns={tableColumns}
								withSelectRow
								onSelect={val => setSelectedRecord(val)}
							/>
						</Col>
						{(selectedRecord && !isFetching) &&
							<Col xs={2}>
								<Form key={selectedRecord?.id} initialValues={selectedRecord} onSubmit={handleUpdate} smartSubmit>
									<NumberInput
										field='shouldAccount'
										label={t('cash_book_section.strings.account')}
										format='currency'
										withControls={false}
										shrink
									/>
									<NumberInput
										field='haveAccount'
										label={t('cash_book_section.strings.account_offset')}
										format='currency'
										withControls={false}
										shrink
									/>
								</Form>
							</Col>
						}
					</Row>
				</ContentBlockLoading>
			}
			{reportError &&
				<AlertModal
					onClose={() => setReportError()}
					text={reportError}
				/>
			}
		</>
	)
}

export default CashBookHandler
